import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlarmClock,
  AppWindow,
  Bell,
  Box,
  CalendarDays,
  Cast,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clapperboard,
  Clock3,
  Coffee,
  Copy,
  Dumbbell,
  Globe,
  GripVertical,
  Image as ImageIcon,
  Info,
  LayoutGrid,
  List,
  Map as MapIcon,
  MapPin,
  Martini,
  MoreHorizontal,
  Music,
  Newspaper,
  Pencil,
  Phone,
  Plus,
  Presentation,
  Search,
  Settings,
  Ship,
  Smartphone,
  Sparkles,
  Star,
  SunMedium,
  Trash2,
  Tv,
  Umbrella,
  UtensilsCrossed,
  Waves,
  Baby,
  Bed,
  Bus,
  Banknote,
  Film,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { api, type Site } from '../api/client';
import { NeedsSite } from '../app/Layout';
import { useSession, useSite } from '../app/session';
import { ask } from '../components/Confirm';
import { cleanValues, EntityForm, type FieldSpec, useAssetUrls } from '../components/EntityForm';
import { Menu } from '../components/Menu';
import { Select } from '../components/Select';
import { Empty, ErrorBox, Field, Loading, PageHeader, Toggle } from '../components/ui';

interface AppTile {
  id: string;
  kind: 'MODULE' | 'WORLD_CLOCK' | 'ANDROID_APP' | 'TV_APP';
  label: string;
  subtitle: string | null;
  icon: string;
  color: string | null;
  imageAssetId: string | null;
  target: string | null;
  enabled: boolean;
}

const MODULES = [
  ['LIVE_TV', 'Live TV'],
  ['CHANNEL_LIST', 'Channel List'],
  ['VOD', 'VOD – Movies'],
  ['AOD', 'AOD – Music'],
  ['FACILITIES', 'Hotel Facilities'],
  ['RESTAURANTS', 'Restaurants & Bars'],
  ['HOTEL_INFO', 'Hotel Information'],
  ['WAKEUP', 'Wake-up'],
] as const;

/** Admin glyphs for the TV icon keys (the TV draws its own icon set). */
const GLYPH: Record<string, LucideIcon> = {
  tv: Tv, list: List, film: Clapperboard, music: Music, bell: Bell, dining: UtensilsCrossed, info: Info, apps: LayoutGrid,
  alarm: AlarmClock, star: Star, spa: Sparkles, map: MapIcon, weather: SunMedium, globe: Globe, cast: Cast, settings: Settings,
  pool: Waves, fitness: Dumbbell, beach: Umbrella, meeting: Presentation, kids: Baby, shuttle: Bus, bed: Bed, location: MapPin,
  phone: Phone, gallery: ImageIcon, currency: Banknote, news: Newspaper, calendar: CalendarDays, cocktail: Martini, coffee: Coffee,
  youtube: Clapperboard, netflix: Film, clock: Clock3, ship: Ship,
};
export const Glyph = ({ name, size = 20 }: { name: string; size?: number }) => {
  const I = GLYPH[name] ?? AppWindow;
  return <I size={size} />;
};

const SOURCE: Record<AppTile['kind'], { label: string; icon: ReactNode; fg: string; bg: string }> = {
  ANDROID_APP: { label: 'Android app', icon: <Smartphone size={13} />, fg: '#047857', bg: '#e7f8f1' },
  TV_APP: { label: 'TV app', icon: <Tv size={13} />, fg: '#b45309', bg: '#fff4e5' },
  WORLD_CLOCK: { label: 'Built-in', icon: <Box size={13} />, fg: '#6d28d9', bg: '#f1ebff' },
  MODULE: { label: 'Module', icon: <LayoutGrid size={13} />, fg: '#1d4ed8', bg: '#e8f0ff' },
};
const opensOf = (a: AppTile) =>
  a.kind === 'MODULE' ? `Opens ${MODULES.find(([k]) => k === a.target)?.[1] ?? a.target ?? '—'}` : a.kind === 'WORLD_CLOCK' ? 'Built-in world clock' : a.kind === 'TV_APP' ? `${a.target ?? 'App'} built into the TV` : 'Installed Android app';
const targetOf = (a: AppTile) => (a.kind === 'WORLD_CLOCK' ? 'world-clock' : a.target ?? '—');

const appFields: FieldSpec[] = [
  {
    key: 'kind',
    label: 'Opens',
    type: 'select',
    options: [
      { value: 'MODULE', label: 'A PulseIPTV module' },
      { value: 'WORLD_CLOCK', label: 'World clock (built in)' },
      { value: 'ANDROID_APP', label: 'An installed Android app' },
      { value: 'TV_APP', label: 'An app built into the TV (LG)' },
    ],
  },
  {
    key: 'target',
    label: 'Module or app',
    type: 'custom',
    render: (v, set) =>
      v.kind === 'MODULE' ? (
        <Field label="Module">
          <Select value={(v.target as string) ?? ''} onChange={(e) => set({ target: e.target.value })}>
            <option value="">Choose…</option>
            {MODULES.map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </Select>
        </Field>
      ) : v.kind === 'TV_APP' ? (
        <Field label="App name on the TV" hint="As the TV lists it, e.g. YouTube. Opens on LG hotel TVs that have the app built in.">
          <input value={(v.target as string) ?? ''} onChange={(e) => set({ target: e.target.value })} />
        </Field>
      ) : v.kind === 'ANDROID_APP' ? (
        <Field label="Android package" hint="e.g. com.google.android.youtube.tv. Only works on Android boxes where the app is installed.">
          <input value={(v.target as string) ?? ''} onChange={(e) => set({ target: e.target.value.trim() })} />
        </Field>
      ) : null,
  },
  { key: 'label', label: 'Title', type: 'text', required: true, placeholder: 'World Clock' },
  { key: 'subtitle', label: 'Short line', type: 'text', placeholder: 'Check the time anywhere' },
  { key: 'icon', label: 'Icon', type: 'icon' },
  { key: 'color', label: 'Icon tile colour', type: 'color' },
  { key: 'imageAssetId', label: 'Background photo', type: 'image', urlKey: 'imageUrl' },
  { key: 'enabled', label: 'Shown on TV', type: 'toggle' },
];

function StatCard({ icon, tone, value, label, sub }: { icon: ReactNode; tone: string; value: number; label: string; sub: string }) {
  return (
    <div className="stat-tile">
      <span className={`stat-tile-icon ${tone}`}>{icon}</span>
      <span>
        <span className="stat-tile-label">{label}</span>
        <span className="stat-tile-value">{value}</span>
        <span className="stat-tile-sub">{sub}</span>
      </span>
    </div>
  );
}

const AppIcon = ({ a, size = 44 }: { a: AppTile; size?: number }) => (
  <span className="app-icon" style={{ width: size, height: size, background: a.color ?? '#7C3AED' }}>
    <Glyph name={a.icon} size={Math.round(size * 0.5)} />
  </span>
);

export function AppsPage() {
  const site = useSite();
  return <NeedsSite>{site ? <Apps key={site.id} site={site} /> : <PageHeader title="Apps" />}</NeedsSite>;
}

function Apps({ site }: { site: Site }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const url = useAssetUrls();
  const key = ['apps', site.id];
  const query = useQuery({ queryKey: key, queryFn: () => api.get<{ items: AppTile[] }>(`/sites/${site.id}/apps`).then((r) => r.items) });
  const invalidate = () => qc.invalidateQueries({ queryKey: key });
  const [order, setOrder] = useState<AppTile[] | null>(null);
  const items = order ?? query.data ?? [];
  const [q, setQ] = useState('');
  const [src, setSrc] = useState('all');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('order');
  const [drag, setDrag] = useState<number | null>(null);
  const [selId, setSelId] = useState<string | null>(null);
  const [edit, setEdit] = useState<AppTile | 'new' | null>(null);
  const editable = can('ADMIN');

  const toForm = (a: AppTile) => ({ ...a, imageUrl: url(a.imageAssetId) });
  const body = (a: AppTile) => cleanValues(toForm(a), appFields);
  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.put(`/sites/${site.id}/apps/order`, { ids }),
    onSettled: async () => {
      await invalidate();
      setOrder(null);
    },
  });
  // App tiles are saved whole (the server re-checks the module / package).
  const toggle = useMutation({ mutationFn: (a: AppTile) => api.put(`/apps/${a.id}`, { ...body(a), enabled: !a.enabled }), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/apps/${id}`), onSuccess: invalidate });
  const duplicate = useMutation({ mutationFn: (a: AppTile) => api.post(`/sites/${site.id}/apps`, { ...body(a), label: `${a.label} copy`.slice(0, 40) }), onSuccess: invalidate });
  const moveTo = (from: number, to: number) => {
    if (to < 0 || to >= items.length || from === to) return;
    const next = [...items];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m!);
    reorder.mutate(next.map((x) => x.id));
    setOrder(next);
  };

  const filtering = q.trim() !== '' || src !== 'all' || status !== 'all' || sort !== 'order';
  const shown = items
    .filter((a) => {
      const s = q.trim().toLowerCase();
      if (s && ![a.label, a.subtitle, a.target].some((x) => x?.toLowerCase().includes(s))) return false;
      if (src !== 'all' && a.kind !== src) return false;
      if (status === 'on' && !a.enabled) return false;
      if (status === 'off' && a.enabled) return false;
      return true;
    })
    .sort((a, b) => (sort === 'name' ? a.label.localeCompare(b.label) : sort === 'source' ? a.kind.localeCompare(b.kind) : 0));
  const sel = items.find((a) => a.id === selId) ?? shown[0] ?? null;

  if (query.isLoading) return <Loading />;
  return (
    <>
      <PageHeader
        title="Apps"
        sub="The launcher tiles on the TV: shortcuts to modules, built-in apps and installed Android apps."
        actions={
          editable && (
            <button className="btn primary" onClick={() => setEdit('new')}>
              <Plus size={16} /> Add app tile
            </button>
          )
        }
      />
      <div className="stat-strip">
        <StatCard icon={<LayoutGrid size={22} />} tone="violet" value={items.length} label="Total app tiles" sub="All tiles in the launcher" />
        <StatCard icon={<CheckCircle2 size={22} />} tone="green" value={items.filter((a) => a.enabled).length} label="Enabled apps" sub="Visible on TV" />
        <StatCard icon={<Box size={22} />} tone="blue" value={items.filter((a) => a.kind !== 'ANDROID_APP').length} label="Built-in & modules" sub="Work on every TV" />
        <StatCard icon={<Smartphone size={22} />} tone="green" value={items.filter((a) => a.kind === 'ANDROID_APP').length} label="Android apps" sub="Android boxes only" />
      </div>

      <div className="fac-layout">
        <div className="card fill">
          <div className="pkg-head">
            <span className="row" style={{ gap: 8, flex: 1 }}>
              <span className="pkg-head-title">App tiles</span>
              <span className="pkg-count">{items.length}</span>
            </span>
            <span className="muted" style={{ fontSize: 13 }}>
              Drag to change the order on the TV
            </span>
          </div>
          <div className="filters" style={{ marginTop: 14 }}>
            <label className="search">
              <Search size={16} />
              <input placeholder="Search apps by name or package…" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <Select value={src} onChange={(e) => setSrc(e.target.value)} aria-label="Source">
              <option value="all">All sources</option>
              <option value="MODULE">Modules</option>
              <option value="WORLD_CLOCK">Built-in</option>
              <option value="ANDROID_APP">Android apps</option>
            </Select>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
              <option value="all">All statuses</option>
              <option value="on">Enabled</option>
              <option value="off">Disabled</option>
            </Select>
            <Select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort">
              <option value="order">Custom order</option>
              <option value="name">Name</option>
              <option value="source">Source</option>
            </Select>
          </div>
          {shown.length === 0 ? (
            <Empty>{items.length ? 'No app matches.' : 'No app tiles yet. Add World Clock, a module shortcut or an Android app such as YouTube.'}</Empty>
          ) : (
            <div className="table-wrap">
              <table className="fac-table">
                <thead>
                  <tr>
                    <th style={{ width: 30 }} />
                    <th>App</th>
                    <th>Source</th>
                    <th>Package / module</th>
                    <th>Opens</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((a) => {
                    const i = items.indexOf(a);
                    const s = SOURCE[a.kind];
                    return (
                      <tr
                        key={a.id}
                        className={`${sel?.id === a.id ? 'selected' : ''}${drag === i ? ' dragging' : ''}`}
                        style={!a.enabled ? { opacity: 0.55 } : undefined}
                        onClick={() => setSelId(a.id)}
                        draggable={editable && !filtering}
                        onDragStart={(e) => {
                          setDrag(i);
                          setOrder(items);
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        onDragOver={(e) => {
                          if (drag === null || drag === i) return;
                          e.preventDefault();
                          const next = [...items];
                          const [m] = next.splice(drag, 1);
                          next.splice(i, 0, m!);
                          setOrder(next);
                          setDrag(i);
                        }}
                        onDragEnd={() => {
                          setDrag(null);
                          if (order) reorder.mutate(order.map((x) => x.id));
                        }}
                      >
                        <td>
                          <span className="drag-handle" title={filtering ? 'Clear search, filters and sort to reorder' : 'Drag to reorder'}>
                            <GripVertical size={16} />
                          </span>
                        </td>
                        <td>
                          <span className="row" style={{ gap: 12, flexWrap: 'nowrap' }}>
                            <AppIcon a={a} />
                            <span style={{ minWidth: 0 }}>
                              <strong style={{ display: 'block' }}>{a.label}</strong>
                              {a.subtitle && <span className="muted fac-sub">{a.subtitle}</span>}
                            </span>
                          </span>
                        </td>
                        <td>
                          <span className="chip" style={{ color: s.fg, background: s.bg, whiteSpace: 'nowrap', display: 'inline-flex', gap: 5, alignItems: 'center' }}>
                            {s.icon} {s.label}
                          </span>
                        </td>
                        <td className="mono" style={{ fontSize: 12.5 }}>
                          {targetOf(a)}
                        </td>
                        <td className="muted" style={{ fontSize: 13 }}>
                          {opensOf(a)}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <Toggle on={a.enabled} onChange={() => editable && toggle.mutate(a)} label="Shown on TV" />
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                          {editable && (
                            <>
                              <button className="icon-btn" title="Edit" onClick={() => setEdit(a)}>
                                <Pencil size={16} />
                              </button>
                              <button className="icon-btn" title="Duplicate" onClick={() => duplicate.mutate(a)}>
                                <Copy size={16} />
                              </button>
                              <button className="icon-btn danger-icon" title="Delete" onClick={() => ask(`Delete the ${a.label} tile? It disappears from the TVs.`, () => remove.mutate(a.id))}>
                                <Trash2 size={16} />
                              </button>
                              <Menu
                                minWidth={180}
                                button={({ onClick }) => (
                                  <button type="button" className="icon-btn" title="More" onClick={onClick}>
                                    <MoreHorizontal size={16} />
                                  </button>
                                )}
                              >
                                <button type="button" className="more-item" disabled={i === 0} onClick={() => moveTo(i, 0)}>
                                  <ChevronUp size={15} /> Move to top
                                </button>
                                <button type="button" className="more-item" disabled={i === items.length - 1} onClick={() => moveTo(i, items.length - 1)}>
                                  <ChevronDown size={15} /> Move to bottom
                                </button>
                              </Menu>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="muted" style={{ fontSize: 13, marginTop: 12 }}>
            Showing <strong>{shown.length}</strong> of {items.length} apps
          </div>
          <ErrorBox error={toggle.error ?? remove.error ?? reorder.error ?? duplicate.error} />
        </div>

        <div className="card fac-detail">
          <h3 style={{ margin: 0 }}>App preview & details</h3>
          <div className="apps-tv">
            <div className="apps-tv-grid">
              {items
                .filter((a) => a.enabled)
                .map((a) => (
                  <span key={a.id} className={`apps-tv-tile${a.id === sel?.id ? ' on' : ''}`} onClick={() => setSelId(a.id)}>
                    <span className="apps-tv-icon" style={{ background: a.color ?? '#7C3AED' }}>
                      <Glyph name={a.icon} size={18} />
                    </span>
                    <span className="apps-tv-label">{a.label}</span>
                  </span>
                ))}
            </div>
          </div>
          <div className="info-tv-stand" />
          {sel ? (
            <>
              <div className="row" style={{ gap: 12, marginTop: 16, flexWrap: 'nowrap' }}>
                <AppIcon a={sel} size={48} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ display: 'block', fontSize: 16 }}>{sel.label}</strong>
                  {sel.subtitle && <span className="muted" style={{ fontSize: 13 }}>{sel.subtitle}</span>}
                </span>
                <span className={`badge ${sel.enabled ? 'ok' : ''}`}>{sel.enabled ? 'Enabled' : 'Disabled'}</span>
              </div>
              <div className="fac-detail-list">
                <div className="fac-detail-row">
                  <span>Source</span>
                  <span className="fac-detail-val">
                    <span className="chip" style={{ color: SOURCE[sel.kind].fg, background: SOURCE[sel.kind].bg }}>
                      {SOURCE[sel.kind].label}
                    </span>
                  </span>
                </div>
                <div className="fac-detail-row">
                  <span>{sel.kind === 'ANDROID_APP' ? 'Package name' : 'Opens'}</span>
                  <span className="fac-detail-val mono" style={{ fontSize: 12.5 }}>
                    {sel.kind === 'ANDROID_APP' ? sel.target : opensOf(sel).replace(/^Opens /, '')}
                    {sel.kind === 'ANDROID_APP' && sel.target && (
                      <button className="icon-btn" title="Copy" style={{ marginLeft: 4 }} onClick={() => void navigator.clipboard?.writeText(sel.target!)}>
                        <Copy size={14} />
                      </button>
                    )}
                  </span>
                </div>
                <div className="fac-detail-row">
                  <span>Visible on</span>
                  <span className="fac-detail-val">Apps page</span>
                </div>
                <div className="fac-detail-row">
                  <span>Position</span>
                  <span className="fac-detail-val">
                    {items.indexOf(sel) + 1} of {items.length}
                  </span>
                </div>
                {sel.kind === 'ANDROID_APP' && (
                  <div className="muted" style={{ fontSize: 12.5, marginTop: 6 }}>
                    Android apps only open on Android boxes where the app is installed. LG TVs skip this tile.
                  </div>
                )}
              </div>
              {editable && (
                <button className="btn primary" style={{ width: '100%', justifyContent: 'center', marginTop: 16 }} onClick={() => setEdit(sel)}>
                  <Pencil size={16} /> Edit app
                </button>
              )}
            </>
          ) : null}
        </div>
      </div>

      {edit && (
        <EntityForm
          title={edit === 'new' ? 'Add app tile' : `Edit ${edit.label}`}
          fields={appFields}
          wide
          initial={edit === 'new' ? { kind: 'MODULE', icon: 'apps', color: '#7C3AED', enabled: true } : toForm(edit)}
          onClose={() => setEdit(null)}
          onSave={async (values) => {
            const b = cleanValues(values, appFields);
            if (edit === 'new') await api.post(`/sites/${site.id}/apps`, b);
            else await api.put(`/apps/${edit.id}`, b);
            await invalidate();
          }}
        />
      )}
    </>
  );
}
